// Port Wingo UI (React 19 + Tailwind 4) → Tailwind 3.4, preluat din portul ERP-ului (React 18). Vezi components/wingo/README.md.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type RefCallback } from "react";
import { refCleanup } from "@/components/wingo/lib/with-ref";

// Wingo UI Pro · https://wingo-ui.com/legal/license

// which items go into the overflow menu first. end: the last one (actions, tabs). start: the first one.
// middle: from the second item onward, keeping the first and the last ones (breadcrumbs: Home / … / Parent / Current).
// or an explicit list of indexes, first to collapse first
export type OverflowCollapseOrder = "end" | "start" | "middle" | readonly number[];

export type UseOverflowItemsOptions = {
  // how many items the row renders
  count: number;
  // default "end"
  collapseOrder?: OverflowCollapseOrder;
  // indexes that never collapse (the primary action, the current breadcrumb)
  pinned?: readonly number[];
  // never collapse below this many visible items. default 0
  minVisible?: number;
  // px between items. default: the container's computed column-gap
  gap?: number;
  // px of the row kept free for something you do not measure. default 0
  reserve?: number;
  // show everything and stop measuring (e.g. below a breakpoint where CSS already switched the layout)
  disabled?: boolean;
};

// spread onto each item and onto the overflow trigger. a hidden item stays mounted, invisible and out of the flow, so
// it can still be measured and comes back the moment there is room
export type OverflowItemProps = {
  ref: RefCallback<HTMLElement>;
  "data-overflow": "visible" | "hidden";
  "aria-hidden"?: true;
  // React 19 (portal): `inert` e boolean (vezi inertProps în lib/with-ref.ts)
  inert?: boolean;
  style?: CSSProperties;
};

export type UseOverflowItemsResult = {
  // the row whose width is the budget
  containerRef: RefCallback<HTMLElement>;
  getItemProps: (index: number) => OverflowItemProps;
  // the "…" / "More" trigger: measured all the time, shown only while something overflows
  getMoreProps: () => OverflowItemProps;
  isHidden: (index: number) => boolean;
  // collapsed indexes in ascending order: render them in the overflow menu
  hiddenIndexes: number[];
  overflowing: boolean;
  // false on the server and until the first measurement: everything renders, so keep the row overflow-clipped
  measured: boolean;
};

const HIDDEN_STYLE: CSSProperties = {
  position: "absolute",
  top: 0,
  insetInlineStart: -9999,
  width: "max-content",
  visibility: "hidden",
  pointerEvents: "none",
};

// layout effects warn on the server; effects are fine there because nothing is measured
const useIsomorphicLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

function orderFor(collapseOrder: OverflowCollapseOrder, count: number): number[] {
  if (typeof collapseOrder !== "string") return collapseOrder.filter((i) => i >= 0 && i < count);
  const all = Array.from({ length: count }, (_, i) => i);
  if (collapseOrder === "start") return all;
  if (collapseOrder === "middle") return all.slice(1, Math.max(1, count - 1));
  return all.reverse();
}

function contentWidth(el: HTMLElement) {
  const style = getComputedStyle(el);
  return el.clientWidth - (parseFloat(style.paddingInlineStart) || 0) - (parseFloat(style.paddingInlineEnd) || 0);
}

function sameList(a: number[], b: number[]) {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

// priority+ overflow: shows as many items of a row as fit and collapses the rest (in collapseOrder) into an overflow
// trigger. measures real widths with ResizeObserver, so labels, fonts and zoom are always right, and re-renders only
// when the set of hidden items changes
export function useOverflowItems({
  count,
  collapseOrder = "end",
  pinned,
  minVisible = 0,
  gap,
  reserve = 0,
  disabled = false,
}: UseOverflowItemsOptions): UseOverflowItemsResult {
  const [hidden, setHidden] = useState<number[]>([]);
  const [measured, setMeasured] = useState(false);
  const container = useRef<HTMLElement | null>(null);
  const items = useRef<(HTMLElement | null)[]>([]);
  const more = useRef<HTMLElement | null>(null);
  const observer = useRef<ResizeObserver | null>(null);
  const frame = useRef(0);

  // primitive keys, so an inline array or a new pinned list each render does not re-measure
  const orderKey = typeof collapseOrder === "string" ? collapseOrder : `[${collapseOrder.join(",")}]`;
  const pinnedKey = pinned?.join(",") ?? "";

  const compute = useCallback(() => {
    const row = container.current;
    if (!row || disabled) {
      setHidden((prev) => (prev.length ? [] : prev));
      return;
    }
    const pinnedSet = new Set(pinnedKey ? pinnedKey.split(",").map(Number) : []);
    const parsed: OverflowCollapseOrder = orderKey.startsWith("[")
      ? orderKey.length > 2
        ? orderKey.slice(1, -1).split(",").map(Number)
        : []
      : (orderKey as OverflowCollapseOrder);
    const order = orderFor(parsed, count).filter((i) => !pinnedSet.has(i));
    const space = gap ?? (parseFloat(getComputedStyle(row).columnGap) || 0);
    const budget = contentWidth(row) - reserve;
    const widths = Array.from({ length: count }, (_, i) => items.current[i]?.getBoundingClientRect().width ?? 0);
    const moreWidth = more.current?.getBoundingClientRect().width ?? 0;

    let sum = widths.reduce((total, w) => total + w, 0);
    let visible = count;
    const next = new Set<number>();
    const fits = () => sum + space * Math.max(0, visible - 1) + (next.size ? space + moreWidth : 0) <= budget + 0.5;
    for (const index of order) {
      if (fits() || visible <= minVisible) break;
      next.add(index);
      sum -= widths[index];
      visible -= 1;
    }
    const list = Array.from(next).sort((a, b) => a - b);
    setHidden((prev) => (sameList(prev, list) ? prev : list));
    setMeasured(true);
  }, [count, orderKey, pinnedKey, minVisible, gap, reserve, disabled]);

  // resize callbacks arrive in bursts; one measurement per frame is enough
  const schedule = useCallback(() => {
    if (frame.current) return;
    frame.current = requestAnimationFrame(() => {
      frame.current = 0;
      compute();
    });
  }, [compute]);

  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(schedule);
    observer.current = ro;
    if (container.current) ro.observe(container.current);
    for (const el of items.current) if (el) ro.observe(el);
    if (more.current) ro.observe(more.current);
    return () => {
      ro.disconnect();
      observer.current = null;
      cancelAnimationFrame(frame.current);
      frame.current = 0;
    };
  }, [schedule]);

  // measure before the first paint and whenever the inputs change, so a row never flashes too wide
  useIsomorphicLayoutEffect(() => {
    compute();
  }, [compute]);

  const track = useCallback((el: HTMLElement | null) => {
    if (el) observer.current?.observe(el);
    return () => {
      if (el) observer.current?.unobserve(el);
    };
  }, []);

  // React 18: ref callback-urile cu cleanup trec prin refCleanup (lib/with-ref.ts)
  const containerRef = useMemo<RefCallback<HTMLElement>>(
    () =>
      refCleanup((el: HTMLElement) => {
        container.current = el;
        return track(el);
      }),
    [track],
  );

  // one stable ref callback per index, so items do not re-attach on every render
  const itemRefs = useRef<RefCallback<HTMLElement>[]>([]);
  const itemRef = useCallback(
    (index: number) => {
      let ref = itemRefs.current[index];
      if (!ref) {
        ref = refCleanup((el: HTMLElement) => {
          items.current[index] = el;
          const untrack = track(el);
          return () => {
            if (items.current[index] === el) items.current[index] = null;
            untrack();
          };
        });
        itemRefs.current[index] = ref;
      }
      return ref;
    },
    [track],
  );

  const moreRef = useMemo<RefCallback<HTMLElement>>(
    () =>
      refCleanup((el: HTMLElement) => {
        more.current = el;
        return track(el);
      }),
    [track],
  );

  return useMemo(() => {
    const hiddenSet = new Set(hidden);
    const props = (ref: RefCallback<HTMLElement>, isHidden: boolean): OverflowItemProps =>
      isHidden
        ? { ref, "data-overflow": "hidden", "aria-hidden": true, inert: true, style: HIDDEN_STYLE }
        : { ref, "data-overflow": "visible" };
    return {
      containerRef,
      getItemProps: (index: number) => props(itemRef(index), hiddenSet.has(index)),
      getMoreProps: () => props(moreRef, hiddenSet.size === 0),
      isHidden: (index: number) => hiddenSet.has(index),
      hiddenIndexes: hidden,
      overflowing: hidden.length > 0,
      measured,
    };
  }, [hidden, measured, containerRef, itemRef, moreRef]);
}
